import type * as OSS from 'ali-oss';
import OssClient from 'ali-oss';
import { cancelUploadFile, getDownloadBucket } from '~/api';
import { registerUnloadEvent } from '~/utils';

interface IAbort {
  status: number;
  name: string;
}

function hasKey(obj: XOSS.Loose, key: string) {
  return Object.prototype.hasOwnProperty.call(obj, key);
}
export default class XOSSUpload {
  private TAG = '[XOSSUpload]';
  private auth!: XOSS.IAuth;
  private client!: OSS;
  private cpt = {
    name: '',
    uploadId: '', // AliOSS uploadId，用于取消上传
  };

  private callbackConfig = {
    host: '',
    url: '',
  };

  private dataSource!: XOSS.IDataSource;
  private eta = 0; // 预计完成时间，用于处理 99%
  file!: File;
  private interval = 3000; // 状态轮询间隔，3 秒
  private media!: XOSS.IMedia;
  private params!: XOSS.IBizParams;
  private progress = {
    current: 0,
    delta: 0,
  };

  private ignore_query_status = false;
  private is_transcoding = false; // 默认资源不转码
  private timer = 0;
  private ossUploadDone = false; // 上传 oss 是否完成
  private uid = '';
  upload_secret_id = '';
  private upload_max_percent = 0; // 上传 oss 最大进度值

  private onProgress: XOSS.TOnProgress = (percent: number, extras: XOSS.IExtras) => {
    console.log(this.TAG, percent, extras);
  };

  private onOssSuccess = () => {
    console.log(this.TAG, 'OSS上传完成：');
  };

  private onSuccess: XOSS.TOnSuccess = (media: XOSS.ISuccess) => {
    console.log(this.TAG, '上传完成：', media);
  };

  private onFail: XOSS.TOnFail = (msg: string, extras: XOSS.IExtras) => {
    console.log(this.TAG, msg, extras);
  };

  private static queryMaxPercent = 99;

  constructor(options: XOSS.IServiceOptions) {
    const {
      auth,
      dataSource,
      is_transcoding,
      params,
      onProgress,
      onOssSuccess,
      onSuccess,
      onFail,
    } = options;
    if (!options.auth) {
      console.error(this.TAG, '初始化需要: 用户信息 user_id');
      return;
    }
    if (!options.dataSource) {
      console.error(this.TAG, '初始化需要：实现 dataSource');
      return;
    }
    this.auth = auth;
    this.dataSource = dataSource;
    if (is_transcoding !== undefined) {
      this.is_transcoding = is_transcoding;
    }

    this.params = params;
    if (onProgress) {
      this.onProgress = onProgress;
    }

    if (onSuccess) {
      this.onSuccess = onSuccess;
    }

    if (onOssSuccess) {
      this.onOssSuccess = onOssSuccess;
    }

    if (onFail) {
      this.onFail = onFail;
    }

    console.log(this.TAG, '初始化成功');
  }

  private _getTimeName(name?: string) {
    return `${this.TAG} ${this.upload_secret_id} ${this.uid}${name ? ` ${name}` : ''}`;
  }

  /**
   * 实例化 oss
   */
  private _genClient(res: XOSS.IPrepareSuccess) {
    return new OssClient({
      accessKeyId: res.credential.access_key_id,
      accessKeySecret: res.credential.access_key_secret,
      bucket: res.credential.bucket,
      region: res.credential.region,
      stsToken: res.credential.security_token,
      refreshSTSToken() {
        return Promise.resolve({
          accessKeyId: res.credential.access_key_id,
          accessKeySecret: res.credential.access_key_secret,
          stsToken: res.credential.security_token,
        });
      },
    });
  }

  /**
   * 生成 oss.callback.body
   */
  private _genBodyTemplate() {
    let templateString = 'bucket=${bucket}';
    templateString += '&object=${object}';
    templateString += '&etag=${etag}';
    templateString += '&size=${size}';
    templateString += '&mimeType=${mimeType}';
    templateString += '&imageInfo.height=${imageInfo.height}';
    templateString += '&imageInfo.width=${imageInfo.width}';
    templateString += '&imageInfo.format=${imageInfo.format}';
    templateString += '&contentMd5=${contentMd5}';
    templateString += '&clientIp=${clientIp}';
    templateString += '&reqId=${reqId}';
    templateString += '&user_id=${x:user_id}';
    templateString += '&is_transcoding=${x:is_transcoding}';
    templateString += '&upload_secret_id=${x:upload_secret_id}';
    templateString += '&upload_path=${x:upload_path}';
    templateString += '&real_filename=${x:real_filename}';
    for (const [k, v] of Object.entries(this.params)) {
      if (typeof v === 'string' && v === '') {
        console.warn(`${k} is empty`);
      } else {
        templateString += `&${k}=$\{x:${k}}`;
      }
    }
    return templateString;
  }

  private _onProgress(p: number) {
    const intPercent = +p.toFixed(0);
    const validPercent = intPercent > 100 ? 100 : intPercent;
    console.log(this._getTimeName(), 'progress:', validPercent);
    this.progress.current = validPercent;
    this.onProgress(validPercent, { uid: this.uid, file: this.file });
  }

  private _onFail(msg: string, info = {}) {
    console.warn('失败:', msg);
    this.onFail(msg, { uid: this.uid, file: this.file, info });
  }

  private _onSuccess() {
    console.timeEnd(this._getTimeName('后端耗时'));
    this.onSuccess({
      uid: this.uid,
      ...this.media,
    });
  }

  private async _multipartUpload(res: XOSS.IPrepareSuccess) {
    if (!this.file) {
      return;
    }

    const { host, url } = this.callbackConfig;
    const { user_id } = this.auth;
    const { resource_type, upload_type, model_type, path_prefix, project_id } = this.params;
    const { upload_secret_id, upload_path } = res;
    this.upload_secret_id = upload_secret_id;
    this.cpt.name = upload_path;
    const client = await this._genClient(res);
    this.client = client;
    const body = this._genBodyTemplate();
    const options = {
      // 获取分片上传进度、断点和返回值
      // cpt / res 暂且不用
      progress: (p: number, cpt: { uploadId: string }) => {
        if (p !== 1) {
          // cpt 在上传完成会为 null
          this.cpt.uploadId = cpt.uploadId;
        } else {
          this.onOssSuccess();
          this.ossUploadDone = true;
        }
        this.progress.current = this.upload_max_percent * p;
        this._onProgress(this.progress.current);
      },
      // TODO: 下面两个参数暂固定，有需要再开放设置
      parallel: this.file.size / Math.pow(1024, 2) < 1 ? 1 : 4, // 设置并发上传的分片数量。
      partSize: 1024 * 1024, // 设置分片大小。默认值为 1 MB，最小值为 100 KB
    };
    const customValue = {
      is_transcoding: this.is_transcoding,
      user_id,
      resource_type,
      upload_type,
      upload_secret_id,
      upload_path,
      real_filename: this.file.name,
    };
    if (hasKey(this.params, 'project_id')) {
      Object.assign(customValue, { project_id });
    }

    if (hasKey(this.params, 'model_type')) {
      Object.assign(customValue, { model_type });
    }

    if (hasKey(this.params, 'path_prefix')) {
      Object.assign(customValue, { path_prefix });
    }

    try {
      console.time(this._getTimeName('OSS耗时'));
      const ossRes = await client.multipartUpload(upload_path, this.file, {
        ...options,
        callback: {
          host,
          url,
          body,
          customValue,
        },
      });
      const { data, media } = (<unknown>ossRes) as XOSS.IOSSResponse;
      console.timeEnd(this._getTimeName('OSS耗时'));
      console.time(this._getTimeName('后端耗时'));
      if (Object.keys(data).length === 0) {
        this._prepareQuery();
        return;
      }
      if (data?.upload_status === 0) {
        this._finish({ ...media, relatedObject: ossRes?.related_object });
      }

      if (data?.upload_status === 2) {
        this._backendCancel(8); // sdk上传失败 ,貌似这里调用不到
        this._onFail(data.failed_info.detail, data.failed_info);
      }

      this._prepareQuery();
    } catch (err: any) {
      if (err instanceof TypeError) {
        this._backendCancel(7); // 回调后端失败
        this._onFail(String(err));
      } else if ((err as IAbort)?.name === 'cancel') {
        // !  abortMultipartUpload 会直接进入这里
        this._backendCancel(); // 取消上传，不需要传原因，因为不做记录
      } else if (err.code === 'RequestError') {
        this._backendCancel(8); // sdk上传失败
      } else if (err.code === 'ConnectionTimeoutError') {
        // 超时
        this._backendCancel(9); // sdk上传失败
      } else {
        this._backendCancel(8); // sdk上传失败
      }
    }
  }

  async upload(file: File & { uid: string }) {
    if (!file) {
      return;
    }

    this.uid = file.uid || `uid-${+Date.now()}`;
    this.file = file;
    const { name: filename, size: filesize } = this.file;
    const data = {
      auth_type: 'sts',
      filename,
      filesize,
      ...this.params,
    };
    try {
      const res = await this.dataSource.prepare(data);
      this.callbackConfig.host = res.callback_host;
      this.callbackConfig.url = res.callback_url;
      this.upload_max_percent = res.max_progress_percent;
      this._multipartUpload(res);
    } catch (err: any) {
      console.error(err);

      this.upload_secret_id = err?.upload_secret_id;
      this._onFail(typeof err === 'string' ? err : err?.msg, err?.info);
    }
  }

  async simpleUpload(file: File & { uid: string }) {
    if (!file) {
      return;
    }

    this.uid = file.uid || `uid-${+Date.now()}`;
    this.file = file;
    const { name: filename, size: filesize } = this.file;
    const data = {
      auth_type: 'sts',
      filename,
      filesize,
      ...this.params,
    };
    try {
      const res = await this.dataSource.prepareSimpleUpload(data);
      const { upload_path } = res;
      this.cpt.name = upload_path;
      const client = await this._genClient(res);
      this.client = client;
      const options = {
        // 获取分片上传进度、断点和返回值
        // cpt / res 暂且不用
        progress: (p: number, cpt: { uploadId: string }) => {
          if (p !== 1) {
            // cpt 在上传完成会为 null
            this.cpt.uploadId = cpt.uploadId;
          } else {
            this.ossUploadDone = true;
          }

          this.progress.current = this.upload_max_percent * p;
          this._onProgress(this.progress.current);
        },
        // TODO: 下面两个参数暂固定，有需要再开放设置
        parallel: this.file.size / Math.pow(1024, 2) < 1 ? 1 : 4, // 设置并发上传的分片数量。
        partSize: 1024 * 1024, // 设置分片大小。默认值为 1 MB，最小值为 100 KB
      };
      try {
        console.time(this._getTimeName('OSS耗时'));
        await client.multipartUpload(upload_path, this.file, options);
        console.timeEnd(this._getTimeName('OSS耗时'));
        this._finish(res as any);
      } catch (err) {
        console.error(err);
        this._onFail(JSON.stringify(err));
      }
    } catch (err) {
      console.error(err);
      this._onFail(typeof err === 'string' ? err : JSON.stringify(err));
    }
  }

  private async _prepareQuery() {
    const res = await this._query();
    if (!res) {
      // ! 查一次状态，成功就不轮询（针对小文件）
      return;
    }
    if (this.ignore_query_status) {
      return;
    }

    this.eta = res.eta * 1000;
    const now = +Date.now();
    const count = Math.ceil((this.eta - now) / this.interval); // 预计轮询次数
    this.progress.delta = (XOSSUpload.queryMaxPercent - this.progress.current) / count;
    console.log(
      this._getTimeName(),
      '预计轮询次数:',
      count,
      ',progress 步长:',
      this.progress.delta,
    );
    this._startQuery();
  }

  private _startQuery() {
    this.timer = window.setTimeout(() => {
      this._query().then(res => {
        if (!res) {
          return;
        }

        const now = +Date.now();
        if (now >= this.eta) {
          this._onProgress(XOSSUpload.queryMaxPercent);
        } else {
          if (this.progress.current + this.progress.delta > XOSSUpload.queryMaxPercent) {
            this._onProgress(XOSSUpload.queryMaxPercent);
          } else {
            this._onProgress(this.progress.current + this.progress.delta);
          }
        }
        this._startQuery();
      });
    }, this.interval);
  }

  private async _query() {
    const res = await this.dataSource.queryStatus({
      upload_secret_id: this.upload_secret_id,
    });
    if (res?.upload_status === 0) {
      this._finish({ ...res.media, relatedObject: res?.related_object });
      return null;
    }
    if (res?.upload_status === 2) {
      this._onFail(res.failed_info.detail);
      return null;
    }
    return res;
  }

  private async _backendCancel(failed_hint?: number) {
    window.clearTimeout(this.timer);
    const response: any = await this.dataSource.cancel({
      upload_secret_id: this.upload_secret_id,
      failed_hint,
    });
    if (response && response?.[0]) {
      const record: any = response?.[0];
      this._onFail(record.failed_info.detail, record.failed_info);
    }
    return response;
  }

  private _finish(media: XOSS.IMedia) {
    this.media = media;
    this._onProgress(100);
    this._onSuccess();
  }

  cancel() {
    if (this.ossUploadDone) {
      return this._backendCancel();
    }

    // FIXME 无法 then，也获取不到返回信息
    return this.client.abortMultipartUpload(this.cpt.name, this.cpt.uploadId);
  }

  remove() {
    return this.dataSource.remove({ upload_secret_id: this.upload_secret_id });
  }
}

export function useRegisterRefreshRemove(uploadSecretIds: string[]) {
  /**
   * 刷新时候，需要取消上次的上传
   */
  const cancelUploadList = (sessionGetItem('cancelUploadList') || []) as string[];

  cancelUploadList.forEach(item => {
    cancelUploadFile({ upload_secret_id: item, failed_hint: 6 });
  });
  sessionRemoveItem('cancelUploadList');

  registerUnloadEvent(() => {
    if (uploadSecretIds.length) {
      let cancelUploadList = sessionGetItem('cancelUploadList') || [];
      cancelUploadList.push(...uploadSecretIds);
      cancelUploadList = Array.from(new Set(cancelUploadList));
      sessionSetItem('cancelUploadList', cancelUploadList);
    }
  });
}

// 获取链接域名
export function getDomain(url: string) {
  const parsedUrl = new URL(url);
  return parsedUrl.origin;
}

// oss下载
export async function downloadOssUrl(url: string, name: string) {
  // const OSS = await import('ali-oss').then((m) => m.default)
  const suffix = url?.split('?')?.[0].split('.').pop().toLowerCase();
  const parsedUrl = new URL(url);

  const res = await getDownloadBucket(parsedUrl.origin);
  const client = new OssClient({
    accessKeyId: res.data.access_key_id,
    accessKeySecret: res.data.access_key_secret,
    bucket: res.data.bucket,
    region: res.data.region,
    stsToken: res.data.security_token,
    // authorizationV4: true,
  });

  const response = {
    'content-disposition': `attachment; filename=${encodeURIComponent(`${name}.${suffix}`)}`,
  };

  const result = client.signatureUrl(parsedUrl.pathname, { response });

  const newTarget = new URL(result);

  await downloadVideo(parsedUrl.origin + newTarget.pathname + newTarget.search);
}
